import axios from 'axios';
import { toast } from 'react-toastify';
import { useEffect, useState, useRef } from 'react';
import { useIsFetching, useQuery, useQueryClient } from 'react-query';
import { addNewClient, fetchAvailableData } from 'api';
import { APP_ROUTES, PFX_SUPPORT_URL } from '../../constants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOnboardingAtom, useOnboardingStepsAtom } from '../../atoms';

// COMPONENTS
import AddClient from './AddClient';
import APIsSetup from './APIsSetup';
import BaseSetup from './BaseSetup';
import Configuration from './Configuration';
import PlatformsSetup from './PlatformsSetup';
import OnboardingSidebar from './OnboardingSidebar';
import ConfirmationPopup from './ConfirmationPopup';

interface OnboardProps {
  setTopBarButton: (button: React.ReactNode) => void;
}

interface GoogleSearchAdsAccount {
  customer_id: string[];
  login_customer_id: string;
}

interface MicrosoftAdsAccount {
  customer_id: string;
  account_id: string;
}

interface GoogleAdsAccount {
  customer_id: string[];
  login_customer_id: string;
}

interface GoogleSearchConsoleAccount {
  properties: string[];
}

interface GoogleAnalyticsAccount {
  property_id: string;
}

// interface AdobeAnalyticsAccount {
//   customer_id: string[];
//   login_customer_id: string;
// }

type AccountConfiguration =
  | GoogleSearchAdsAccount
  | MicrosoftAdsAccount
  | GoogleAdsAccount
  | GoogleSearchConsoleAccount
  | GoogleAnalyticsAccount;
// | AdobeAnalyticsAccount;

interface ApiConfiguration {
  [key: string]: AccountConfiguration;
}

interface Api {
  name: string;
  id: string;
  logo: string;
  accounts: AccountConfiguration[];
}

export const DEFAULT_FORM_STATE = {
  agency: '',
  client: '',
  lineOfBusiness: '',
  market: '',
  region: '',
  new_business: false, // New Business flag
};

const getExistingClient = (
  agencies: Agency[],
  formData: typeof DEFAULT_FORM_STATE
) => {
  let foundClient: Client | undefined;

  const foundAgency = agencies.find(
    ({ id }) => id.toString() === formData.agency
  );

  if (foundAgency) {
    foundClient = foundAgency.clients.find(
      ({ name }) =>
        name.trim().toLowerCase() === formData.client.trim().toLowerCase()
    );
  }

  return foundClient;
};

function Onboard({ setTopBarButton }: OnboardProps) {
  const [searchParams] = useSearchParams();
  const { setCurrentStep: setCurrentStepAtom } = useOnboardingStepsAtom();

  const [isOnboarding, setIsOnboarding] = useOnboardingAtom();

  const [currentStep, setCurrentStep] = useState(1);
  // MODALS
  const [, setShowNewBusiness] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [existingClient, setExistingClient] = useState<Client | null>(null);

  const [formData, setFormData] = useState({ ...DEFAULT_FORM_STATE });

  const [activeModules, setActiveModules] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);

  const [customerId, setCustomerId] = useState<string | null>(null);
  const initialAPIs: Api[] = [
    {
      name: 'Google Search Ads 360',
      id: 'google-search-ads-360',
      logo: `${process.env.PUBLIC_URL}/static/img/search_ads.png`,
      accounts: [{ customer_id: [''], login_customer_id: '' }],
    },
    {
      name: 'Google Ads',
      id: 'google-ads',
      logo: `${process.env.PUBLIC_URL}/static/img/google_ads.png`,
      accounts: [{ customer_id: [''], login_customer_id: '' }],
    },
    {
      name: 'Google Search Console',
      id: 'google-search-console',
      logo: `${process.env.PUBLIC_URL}/static/img/search_console.png`,
      accounts: [{ properties: [''] }],
    },
    {
      name: 'Google Analytics',
      id: 'google-analytics',
      logo: `${process.env.PUBLIC_URL}/static/img/analytics.png`,
      accounts: [{ property_id: '' }],
    },
    {
      name: 'Microsoft Ads',
      id: 'microsoft-ads',
      logo: `${process.env.PUBLIC_URL}/static/img/microsoft_ads.png`,
      accounts: [{ customer_id: '', account_id: '' }],
    },
    // { name: "Adobe Analytics", id: "adobe-analytics", logo: `${process.env.PUBLIC_URL}/static/img/adobe_analytics.png`, accounts: [{ customer_id: [""], login_customer_id: "" }] },
  ];
  const [googleEmail, setGoogleEmail] = useState('');
  const [apis, setApis] = useState<Api[]>(initialAPIs);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const apisSetupRef = useRef<{ validateAPIs: () => boolean }>(null);
  const [triggerConsumerUpload, setTriggerConsumerUpload] = useState<
    () => Promise<void>
  >(() => Promise.resolve);
  const [triggerCompetitiveUpload, setTriggerCompetitiveUpload] = useState<
    () => Promise<void>
  >(() => Promise.resolve);
  const [triggerAutoOptimizeUpload, setTriggerAutoOptimizeUpload] = useState<
    () => Promise<void>
  >(() => Promise.resolve);
  const [triggerInsightsUpload, setTriggerInsightsUpload] = useState<
    () => Promise<void>
  >(() => Promise.resolve);
  const [hasUploadedFileConInt, setHasUploadedFileConInt] = useState(false);
  const [hasUploadedFileComInt, setHasUploadedFileComInt] = useState(false);
  const [hasUploadedFileOSAB, setHasUploadedFileOSAB] = useState(false);
  const [hasUploadedFileInsights, setHasUploadedFileInsights] = useState(false);
  const [currency, setCurrency] = useState<string>('');

  const isFetching = useIsFetching() > 0;
  const { data: agencies = [], refetch } = useQuery({
    queryKey: ['base_setup_data'],
    queryFn: fetchAvailableData,
  });

  const handleBackToSettingsClick = () => {
    setShowPopup(true);
    setCurrentStepAtom(0);
  };

  const handleConfirm = () => {
    setShowPopup(false);
    navigate('/settings');
  };

  const handleCancel = () => {
    setShowPopup(false);
  };

  const handleNextStep = async () => {
    if (currentStep === 0) {
      try {
        const foundClient = getExistingClient(agencies, formData);

        // Confirm if user wants to continue with existing client
        if (foundClient) {
          setExistingClient(foundClient);
          return;
        }

        await addNewClient(parseInt(formData.agency), formData.client);
        const { data: updatedAgencies = [] } = await refetch();

        const client = updatedAgencies
          .find(({ id }) => id.toString() === formData.agency)
          ?.clients.find(({ name }) => name === formData.client);

        if (!client) {
          alert('Unable to find new client.');
          return;
        }

        setFormData((prev) => ({ ...prev, client: client.id.toString() }));
      } catch (err) {
        let errorMsg = 'Unable to add client.';
        if (axios.isAxiosError(err)) {
          errorMsg = err.response?.data.error;
        }
        alert(errorMsg);
        return;
      }
    }

    if (currentStep === 1) {
      if (
        !formData.agency ||
        !formData.client ||
        !formData.market ||
        !formData.region
      ) {
        alert(
          'Please fill out the agency, client, market and region fields to proceed.'
        );
        return;
      }
    }

    if (currentStep === 2) {
      const isAnyPlatformEnabled = activeModules.some((isEnabled) => isEnabled);
      if (!isAnyPlatformEnabled) {
        alert('Please enable at least one platform to proceed.');
        return;
      }
    }
    // Check if Google ads or Microsoft ads is populated - if Google ads or Micorosoft ads exists check for currency
    const googleAccountDetails = apis?.[1]?.accounts?.[0] as
      | GoogleAdsAccount
      | undefined;

    const isGoogleAdsAccount = !!(
      googleAccountDetails?.customer_id?.[0] &&
      googleAccountDetails?.login_customer_id
    ); // GoogleAds check both fields
    const microsoftAccountDetails = apis?.[4]?.accounts?.[0] as
      | MicrosoftAdsAccount
      | undefined;
    const isMicrosoftAdsAccount = !!microsoftAccountDetails?.customer_id; // Microsoft Ads check

    const isAdsAccount = isMicrosoftAdsAccount || isGoogleAdsAccount;
    // function to check if google ads or microsoft ads is valid i.e., has currency field selected (Only validate if either ads is true)
    const isAdAccountValid = () => isAdsAccount === !!currency;

    // New Business accounts skip steps 2 & 3 in UI.
    // Step #2 is handled setNewBusiness func.
    if (currentStep === 3 || (currentStep === 1 && formData.new_business)) {
      if (
        (!formData.new_business && !apisSetupRef.current?.validateAPIs()) ||
        !isAdAccountValid()
      ) {
        alert('Please correct the errors in API setup to proceed.');
        return;
      }
      await handleSubmit();
    }

    if (currentStep === 4) {
      if (activeModules[2] && !hasUploadedFileConInt) {
        return null;
      }

      if (activeModules[1] && !hasUploadedFileComInt) {
        return null;
      }

      if (activeModules[3] && !hasUploadedFileOSAB) {
        return null;
      }

      if (activeModules[0] && !hasUploadedFileInsights) {
        return null;
      }
    }

    if (currentStep < 4) {
      setCurrentStep((prevStep) => {
        return formData.new_business && prevStep === 1
          ? 4
          : Math.min(prevStep + 1, 4);
      });
    } else {
      try {
        setIsOnboarding(true);

        if (activeModules[2] && hasUploadedFileConInt) {
          await triggerConsumerUpload();
        }

        if (activeModules[1] && hasUploadedFileComInt) {
          await triggerCompetitiveUpload();
        }

        if (activeModules[3] && hasUploadedFileOSAB) {
          await triggerAutoOptimizeUpload();
        }

        if (activeModules[0] && hasUploadedFileInsights) {
          await triggerInsightsUpload();
        }

        toast.success('Onboarding process has been initiated.');

        setCurrentStepAtom(0);
        navigate(APP_ROUTES.SETTINGS, { state: { customerId } });

        fetch('start-onboarding/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ customer_id: customerId }),
        })
          .then((response) => {
            if (response.ok) {
              response.json().then((res) => {
                toast.success(res?.message || 'Onboarding success');
                // The data server may have recreated the dataset.
                queryClient.invalidateQueries(['get_customer_info_sidebar']);
              });
            } else {
              response.json().then((res) => {
                toast.error(
                  () => (
                    <div>
                      <p className="font-bold">
                        {res.error || 'An unknown error occured.'}
                      </p>
                      <p>
                        Please raise an issue via the{' '}
                        <a
                          className="underline"
                          href={PFX_SUPPORT_URL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          OneSearch support site
                        </a>{' '}
                        and include a reference to module and client .
                      </p>
                    </div>
                  ),
                  { autoClose: 8000 }
                );
              });
            }
          })
          .catch((error) => {
            console.error('Error in fetch operation:', error);
            setIsOnboarding(false);
            toast.error('An error occurred during the onboarding process.');
          });
      } catch (error) {
        console.error('Error on the onboarding process', error);
        setIsOnboarding(false);
        toast.error(
          'An error occurred while uploading the configuration file(s).'
        );
      }
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep((prevStep) => {
      if (prevStep === 1) {
        setFormData({ ...DEFAULT_FORM_STATE, new_business: true });
      }
      return formData.new_business && prevStep === 4
        ? 1
        : Math.max(prevStep - 1, formData.new_business ? 0 : 1);
    });
  };

  const handleSubmit = async () => {
    const enabledModules = activeModules
      .map((isEnabled, index) => (isEnabled ? index + 1 : null))
      .filter((index) => index !== null);

    const apiConfig: ApiConfiguration = apis.reduce((acc, api) => {
      const validAccount = api.accounts.find((account) => {
        if ('customer_id' in account && 'login_customer_id' in account) {
          return (
            (account as GoogleSearchAdsAccount | GoogleAdsAccount).customer_id
              .length &&
            (account as GoogleSearchAdsAccount | GoogleAdsAccount)
              .login_customer_id
          );
        } else if ('customer_id' in account && 'account_id' in account) {
          return (
            (account as MicrosoftAdsAccount).customer_id &&
            (account as MicrosoftAdsAccount).account_id
          );
        } else if ('properties' in account) {
          return (account as GoogleSearchConsoleAccount).properties.length;
        } else if ('property_id' in account) {
          return (account as GoogleAnalyticsAccount).property_id;
        } else {
          return false;
        }
      });

      if (validAccount) {
        acc[api.id] = validAccount;
      }
      return acc;
    }, {} as ApiConfiguration);

    const payload = {
      agency_id: formData.agency,
      client_id: formData.client,
      line_of_business_id: formData.lineOfBusiness,
      market: formData.market,
      region: formData.region,
      platform: enabledModules,
      account_configuration: apiConfig,
      new_business: formData.new_business,
      google_email_account: googleEmail,
    };

    if (!payload.market || !payload.region || !payload.platform.length) {
      alert('Please fill out all required fields.');
    } else {
      try {
        let response;
        if (customerId) {
          // Update existing customer information
          response = await axios.put('/add-customer-information', {
            ...payload,
            customer_id: customerId,
            currency_code: currency,
          });
        } else {
          // Create new customer information
          response = await axios.post('/add-customer-information', {
            ...payload,
            currency_code: currency,
          });
        }
        const data = response.data;

        // Reading the customer_id from the response
        setCustomerId(data.customer_id);
        // Client/market/platform feed the sidebar's account picker.
        queryClient.invalidateQueries(['get_customer_info_sidebar']);
      } catch (err) {
        // Handle error functionality #TODO
      }
    }
  };

  useEffect(() => {
    setTopBarButton(
      <div className="border border-thirdgray rounded-[20px] px-8">
        <button
          className="text-sm font-bold flex items-center text-thirdgray"
          onClick={handleBackToSettingsClick}
        >
          <img
            src={`${process.env.PUBLIC_URL}/static/img/right-arrow.svg`}
            alt="arrow"
            className="mr-[6px] rotate-180"
          />
          Cancel
        </button>
      </div>
    );
    return () => setTopBarButton(null);
  }, [setTopBarButton]);

  // HANDLE NEW BUSINESS CONFIRMATION
  const setNewBusiness = (isNewBusiness: boolean) => {
    setFormData((prevState) => ({
      ...prevState,
      new_business: isNewBusiness,
    }));

    if (isNewBusiness) {
      // Preselect only Competitive Intelligence (step #2)
      setActiveModules([false, true, false, false]);

      // Include set to add client
      setCurrentStep(1); // This is been changed to one to skip step 0 (TODO: Remove comment later)
    }

    setShowNewBusiness(false);
  };

  // HANDLE EXISTING NEW BUSINESS CLIENT
  const onConfirmExisting = () => {
    if (!existingClient) return;

    setFormData((prev) => ({ ...prev, client: existingClient.id.toString() }));
    setExistingClient(null);
    setCurrentStep(1);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BaseSetup
            agencies={agencies}
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 2:
        return (
          <PlatformsSetup
            activeModules={activeModules}
            setActiveModules={setActiveModules}
          />
        );
      case 3:
        return (
          <APIsSetup
            ref={apisSetupRef}
            apis={apis}
            setApis={setApis}
            googleEmail={googleEmail}
            setGoogleEmail={setGoogleEmail}
            setCurrency={setCurrency}
            currency={currency}
          />
        );
      case 4:
        return (
          <Configuration
            formData={formData}
            customerId={customerId}
            activeModules={activeModules}
            setTriggerConsumerUpload={setTriggerConsumerUpload}
            setTriggerCompetitiveUpload={setTriggerCompetitiveUpload}
            setTriggerAutoOptimizeUpload={setTriggerAutoOptimizeUpload}
            setTriggerInsightsUpload={setTriggerInsightsUpload}
            setHasUploadedFileConInt={setHasUploadedFileConInt}
            setHasUploadedFileComInt={setHasUploadedFileComInt}
            setHasUploadedFileOSAB={setHasUploadedFileOSAB}
            setHasUploadedFileInsights={setHasUploadedFileInsights}
          />
        );
      default:
        return (
          <BaseSetup
            agencies={agencies}
            formData={formData}
            setFormData={setFormData}
          />
        );
    }
  };

  useEffect(() => {
    const isNewBusiness = searchParams.get('new_business');
    if (isNewBusiness) {
      setNewBusiness(true);
    }
  }, [searchParams]);

  return (
    <div className={`flex ${isFetching || isOnboarding ? 'cursor-wait' : ''}`}>
      <div className="flex-1">
        <div className="text-left">
          {renderStepContent()}
          <div className="flex justify-between pt-6 px-2">
            <button
              type="button"
              className={`flex items-center justify-center text-sm font-bold py-2 px-6 w-[150px] h-[34px] bg-gray300 text-light rounded-[20px] disabled:hover:cursor-not-allowed ${
                isFetching ? 'hover:cursor-wait' : ''
              }`}
              onClick={handlePreviousStep}
              disabled={
                (formData.new_business && currentStep === 1) ||
                (formData.new_business && currentStep === 4) ||
                (!formData.new_business && currentStep === 1)
              }
            >
              PREVIOUS
            </button>
            <button
              type="button"
              className={`flex items-center justify-center text-sm font-bold py-2 px-6 w-[150px] h-[34px] bg-success text-light rounded-[20px] disabled:cursor-not-allowed disabled:opacity-60 ${
                isFetching || isOnboarding
                  ? 'hover:cursor-wait disabled:cursor-wait'
                  : ''
              }`}
              onClick={handleNextStep}
              disabled={isFetching || isOnboarding}
            >
              NEXT
            </button>
          </div>
        </div>
      </div>
      <OnboardingSidebar
        currentStep={currentStep}
        isNewBusiness={formData.new_business}
      />
      {showPopup ? (
        <ConfirmationPopup
          message="Are you sure you want to go back to settings?"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      ) : null}
      {existingClient ? (
        <ConfirmationPopup
          message="Client exists, would you like to move forward with the existing client?"
          onConfirm={onConfirmExisting}
          onCancel={() => setExistingClient(null)}
        />
      ) : null}
    </div>
  );
}

export default Onboard;
